import { inject, Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';

import { TranslateService } from '@ngx-translate/core';
import { NbToastrConfig } from '@nebular/theme';

import { TRootToastConfig, ToastConfig, TOASTR_CONFIG } from '../classes';
import { TToastStatus, TToastText } from '../toast.types';

import { ToastContainerRegistry } from './toast-container-registry.service';

export type TToastParam<Config extends Partial<ToastConfig> = Partial<ToastConfig>> = {
  message: TToastText;
  title?: TToastText;
  options?: Config;
};

@Injectable()
export class ToastService {
  private readonly containerRegistry = inject(ToastContainerRegistry);

  private readonly globalConfig = inject<TRootToastConfig>(TOASTR_CONFIG);

  private readonly translateService = inject(TranslateService);

  /**
   * Shows toast with message, title and user config.
   * */
  show<Param extends TToastParam = TToastParam>(obj: Param) {
    if (!obj.options) {
      obj.options = { status: 'basic' };
    }

    const { defaultStatusIcons, ...data } = this.globalConfig;
    const icon = obj.options.icon || this.getDefaultIcon(obj.options.status!) || '';
    const toastConfig = new ToastConfig({ ...data, ...obj.options, icon, hasIcon: obj.options.hasIcon ?? !!icon });
    const container = this.containerRegistry.get(toastConfig.position);

    return container.attach({ ...obj, title: obj.title || '', config: toastConfig });
  }

  /**
   * Shows success toast with message, title and user config.
   * */
  success(data: TToastParam) {
    data.options = { ...data.options, status: 'success' };

    return this.show(data);
  }

  /**
   * Shows info toast with message, title and user config.
   * */
  info(data: TToastParam) {
    data.options = { ...data.options, status: 'info' };

    return this.show(data);
  }

  /**
   * Shows warning toast with message, title and user config.
   * */
  warning(data: TToastParam) {
    data.options = { ...data.options, status: 'warning' };

    return this.show(data);
  }

  /**
   * Shows danger toast with message, title and user config.
   * */
  danger(data: TToastParam) {
    data.options = { ...data.options, status: 'danger' };

    return this.show(data);
  }

  /**
   * @deprecated Use httpErrorHandler or custom methods instead of this method for handling API errors and showing toasts.
   * Will be deleted.
   */
  apiError(errorResponse: Readonly<HttpErrorResponse>) {
    if (errorResponse.error?.detail) {
      return this.danger({ message: errorResponse.error.detail });
    }

    return this.danger({
      title: this.translateService.instant('common.request.error.title'),
      message: this.translateService.instant('common.request.error.description'),
    });
  }

  private getDefaultIcon(status: string): NbToastrConfig['icon'] {
    if (!this.globalConfig.defaultStatusIcons) {
      return '';
    }

    return this.globalConfig.defaultStatusIcons[status as TToastStatus] || '';
  }
}
